import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl, getLocale } from 'umi';

import { useSelectArrObj } from '@/pages/dashboard/utils/customHook';
import { dimensionWeeklyMetricsTrend, loadWeeklyMetricsTrend } from '@/pages/dashboard/services/overview';
import { formatFilterValues, formatSingleTime } from '@/pages/dashboard/utils/utils';
import { getDefaultUtcDateValue } from "@/pages/dashboard/utils/dateUtils";
import Item from './CardLine';
import { EngagementRevenueMetricsProps } from '../../_config/interface';
import useOverviewLocalesIntl from '../../_config/useOverviewLocalesIntl';
import { dailyRequest } from '../../_config/dailyRequest';
import { overviewMonthlyRequest } from '../../_config/overviewMonthlyRequest';

export default function EngagementRevenueMetrics({
  gameCode,
  filterLoading,
  renderIndex,
  formValue, // 本来只取时间就行，这里防止后面产品要求传递其他筛选器参数，所以直接把所有参数都传递下来
  placeholderId = '',
  eventData = [],
  type = 'DAY',
  // isSmall = false,
  dailyReqType='normal',
  loading
}: EngagementRevenueMetricsProps) {
  const {
    REVENUE_METRICS_TRENDS,
    ENGAGEMENT_METRICS_TRENDS_TAB_LIST,
  } = useOverviewLocalesIntl();
  const { formatMessage } = useIntl(); // 中英切换方法
  // overview 趋势图加分维度按钮
  const separatedOption = useSelectArrObj(formValue);
  // 卡片配置
  const tabConfig = useMemo(() => ENGAGEMENT_METRICS_TRENDS_TAB_LIST[type], [type, getLocale()]);
  // loading
  const [updateLoading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // 配置项
  const [options, setOptions] = useState([
    {
      api: '',
      title: formatMessage({ id: 'overview_Engagement_Metrics_Trends' }),
      tabList: tabConfig,
      defaultData: null,
      defaultTab: tabConfig[0].value,
      defaultTabText: tabConfig[0].text,
      excelFilterName: formatMessage({ id: 'overview_Engagement_Metrics_Trends' }),
    },
    {
      api: '',
      title: formatMessage({ id: 'overview_Revenue_Metrics_Trends' }),
      tabList: REVENUE_METRICS_TRENDS,
      defaultData: null,
      defaultTab: REVENUE_METRICS_TRENDS[0].value,
      defaultTabText: REVENUE_METRICS_TRENDS[0].text,
      excelFilterName: formatMessage({ id: 'overview_Revenue_Metrics_Trends' }),
    },
  ] as any);
  const optionsRef = useRef(options);
  // 当前卡片value
  const activeTabArr = renderIndex === 0 ? tabConfig : REVENUE_METRICS_TRENDS;
  const [active, setActive] = useState(activeTabArr[0].value);
  const [selected, setSelected] = useState('accumulated');
  // 更新tab value
  const updateTabAndSelected = (tab?: string, selected?: string) => {
    setActive(tab);
    setSelected(selected);
  };

  // 更新 tab 对应的 data
  const updateData = (tabs: string[], res: any) => {
    setOptions(options?.map((option, index) => ({
      ...option,
      defaultData: res,

    })));
  };

  // 获取separatedOption
  const getAccumulated = (selected) => {
    const accumulation = {};
    separatedOption?.map((item: any) => accumulation[`by_${  item?.value}`] = (item?.value === selected && item?.value !== 'accumulated'));
    return accumulation;
  };

  // 根据tab映射字段名
  const keyMap = {
    // daily
    'dau': 'active_users',
    // weekly
    'wau': 'active_users',
    'avg.wau': 'avg_active_users',
    // monthly
    'mau': 'active_users',
    'avg.mau': 'avg_active_users',
    // Engagement Metrics Trends
    'new': 'new_users',
    'tru': 'total_users',
    // Revenue Metrics Trends
    'revenue': 'pay_amount',
    'payer': 'paid_users',
    'payer_ratio': 'payer_rate',
  };

  // 更新数据
  const update = async (tab: string | undefined, selected: string | undefined) => {
    updateTabAndSelected(tab, selected);
    const { channel, rangeTime, region, system, zone, language, filterValue, newFilter, diff, timeType } = formValue;
    const metrics = tab;
    const period = type === 'MONTH' ? 'monthly' : type === 'WEEK' ? 'weekly' : 'daily';
    
    const defaultTimeRange =  getDefaultUtcDateValue(type,rangeTime);
    // 非继承，第一次进入页面折线图区14天、6周、6月
    const rangeTimeArr = diff ? defaultTimeRange : formatSingleTime(rangeTime, timeType);
    // 请求参数
    let params = {
      game_code: gameCode,
      start_time: rangeTimeArr?.[0] ?? '',
      end_time: rangeTimeArr?.[1] ?? '',
      filters: {
        zone_list: zone,
        market_list: region?.markets,
        os_list: system,
        channel_list: channel,
        lang_list: language
      },
    } as any;
    if (newFilter) {
      const { rangeTime, ...filters } = filterValue;
      const defaultTimeRange =  getDefaultUtcDateValue(type,rangeTime);
      const rangeTimeArr = diff ? defaultTimeRange : formatSingleTime(rangeTime, timeType);
      const [start, end] = rangeTimeArr;
      params = {
        ...params,
        filters: formatFilterValues(filters),
        start_time: start,
        end_time: end
      };
    }
    // 分维度参数
    const dimensionParams = { ...params, period, };
    params.metrics = [metrics] ?? [];
    // updateLoading(metrics, true);
    setLoading(true);
    const queryData = {
      DAY: dailyRequest.loadMetricsTrend?.[dailyReqType],
      WEEK: loadWeeklyMetricsTrend,
      MONTH: overviewMonthlyRequest.loadMonthlyMetricsTrend[dailyReqType],
    };
    const dimensionData = {
      DAY: dailyRequest.dimensionMetricsTrend?.[dailyReqType],
      WEEK: dimensionWeeklyMetricsTrend,
      MONTH: overviewMonthlyRequest.dimensionMonthlyMetricsTrend[dailyReqType],
    };
    // 切换tab和切换下拉选请求
    if (selected === 'accumulated') {
      queryData[type](params).then((res: any) => {
        updateData(metrics, res);
        setLoading(false);
        setError(false);
      }).catch((e: any) => {
        setLoading(false);
        setError(e);
      });
    } else {
      const metric = tab && (keyMap[tab] ?? tab);
      const accumulation = getAccumulated(selected ? selected : 'accumulated');
      dimensionData[type]({ ...dimensionParams, metric, accumulation }).then((res: any) => {
        // 处理数据 tab字段名映射
        res?.map(item => {
          tab && (item.metrics[tab] = item?.metrics[metric]);
        });
        updateData(metrics, res);
        // setNodata(res?.length === 0 || !res)
        setLoading(false);
        setError(false);
      }).catch((e: any) => {
        setLoading(false);
        setError(e);
      });
    }
  };

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    if (type && type !== 'day') {
      // week、month的tabList配置， 默认day
      const currentTabList = [...options];
      currentTabList[0].tabList = tabConfig;
      currentTabList[0].defaultTab = tabConfig[0].value;
      currentTabList[0].defaultTabText = tabConfig[0].text;
      setOptions(currentTabList);

      updateTabAndSelected(activeTabArr[0].value, 'accumulated');
    }
  }, [type]);

  useEffect(() => {
    const options = optionsRef?.current;
    setOptions([
      {
        ...options[0],
        title: formatMessage({ id: 'overview_Engagement_Metrics_Trends' }),
        tabList: tabConfig,
        excelFilterName: formatMessage({ id: 'overview_Engagement_Metrics_Trends' }),
      },
      {
        ...options[1],
        title: formatMessage({ id: 'overview_Revenue_Metrics_Trends' }),
        tabList: REVENUE_METRICS_TRENDS,
        excelFilterName: formatMessage({ id: 'overview_Revenue_Metrics_Trends' }),
      },
    ]);
  }, [getLocale()]);

  useEffect(() => {
    if (filterLoading) return;
    update(active, selected);
  }, [formValue, filterLoading, renderIndex]);

  const { tabList, defaultTab, defaultTabText, defaultData, excelFilterName } = options[renderIndex];
  
  // const { tabList, defaultTab, defaultTabText, excelFilterName } = options[renderIndex]

  // const defaultData = {
  //   Nodes: [
  //     {
  //       acu: {to_date: '2384.878417', last_week: '2244.156250', last_month: '2100', last_year: '2400'},
  //       date: "20230802",
  //       pcu: {to_date: '4560.000000', last_week: '4116.000000', last_month: '4233', last_year: '3702'}
  //     },
  //     {
  //       acu: {to_date: '2200.878417', last_week: '2 144.156250', last_month: '2000', last_year: '2700'},
  //       date: "20230803",
  //       pcu: {to_date: '4400.000000', last_week: '4216.000000', last_month: '3955', last_year: '3000'}
  //     },
  //     {
  //       acu: {to_date: '2800.878417', last_week: '2988.156250', last_month: '2300', last_year: '2500'},
  //       date: "20230804",
  //       pcu: {to_date: '5400.000000', last_week: '4816.000000', last_month: '5023', last_year: '4100'}
  //     },
  //   ]
  // };

  return (
    <>
      <Item
        placeholderId={placeholderId}
        gameCode={gameCode}
        eventData={eventData}
        selected={selected}
        renderIndex={renderIndex}
        loading={loading||updateLoading}
        error={error}
        // noData={noData}
        update={update}
        excelFilterName={excelFilterName}
        defaultTab={defaultTab}
        defaultTabText={defaultTabText}
        defaultData={defaultData}
        tabList={tabList}
        type={type}
        formValue={formValue}
        separatedOption={separatedOption}
      />
    </>
  );
}

